import React, { useState, useRef, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';
import SectionTitle from '../components/SectionTitle';

const ManifestPage: React.FC = () => {
  // const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'verse' | 'prose'>('verse');
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  // Set up intersection observer for animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          entry.target.querySelectorAll('.fade-in').forEach((el, index) => {
            (el as HTMLElement).style.animationDelay = `${index * 200}ms`;
            el.classList.add('animate');
          });
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // Verse-style manifest (numbered points) with the updated content
  const verseManifest = [
    {
      number: 1,
      headline: "We see what is happening.",
      text: "The digital world no longer belongs to the people who use it.\nIt belongs to the platforms that control it.\nEvery booking, all visibility – it costs.\nNot just money, but independence.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )
    },
    {
      number: 2,
      headline: "Small businesses, freelancers, creatives –",
      text: "they fight daily to remain visible.\nBut the systems are built against them.\nThose who don't pay, are not seen.\nThose who don't participate, are left out.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      number: 3,
      headline: "Visibility has become a commodity.",
      text: "And the largest platforms hold the monopoly.\nThey rent out reach, demand fees,\ncollect data – and control access.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      number: 4,
      headline: "The internet was once free.",
      text: "Today it is a web of contracts, dependencies, and algorithms.\nNot every platform is bad.\nBut the big ones seize control –\nand leave the small ones behind.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
        </svg>
      )
    },
    {
      number: 5,
      headline: "We say: There is another way.",
      text: "We believe in a different digital model.\nOne that empowers people –\nnot their dependency.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      number: 6,
      headline: "We build tools that belong to you.",
      text: "Websites you can manage yourself.\nShops without percentage fees.\nBookings without monthly charges.\nTechnology that makes visible – not hidden.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
        </svg>
      )
    },
    {
      number: 7,
      headline: "We show alternatives,",
      text: "that exist – but hardly anyone knows.\nBecause they are open source.\nBecause they need consultation.\nBecause they don't advertise.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
        </svg>
      )
    },
    {
      number: 8,
      headline: "We are developers, creatives, strategists.",
      text: "We are not a classic agency.\nWe are a movement.\nAnd we work for you –\nso you can be there for your customers.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
        </svg>
      )
    },
    {
      number: 9,
      headline: "You get a real person,",
      text: "not anonymous support.\nYou get consultation and implementation,\nnot a license agreement.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
        </svg>
      )
    },
    {
      number: 10,
      headline: "It's not about technology.",
      text: "It's about dignity.\nIt's about your work.\nYour visibility.\nYour digital freedom.",
      icon: (
        <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12a3 3 0 100-6 3 3 0 000 6z" />
        </svg>
      )
    }
  ];

  // Prose-style manifest (paragraph form)
  const proseManifest = `
    We see what is happening: The digital world no longer belongs to the people who use it – but to the platforms that control it. Visibility costs. Access costs. Not just money, but independence.

    Small businesses, freelancers, and creatives fight daily to be seen. But the digital systems are not made for them. Those who don't pay, are not found. Those who don't play along, remain invisible.

    Visibility has become a commodity – and the largest platforms hold the monopoly. They rent out reach, collect fees, gather data, and direct access. Not every platform is bad. But the big ones make the rules. And for many, only an Guckröhre remains.

    We believe there is another way.

    We believe in a digital model that empowers people – not their dependency. That's why we build tools that belong to you: Websites you can manage yourself. Shops without revenue sharing. Booking systems without ongoing fees. Visibility without subscription coercion.

    The alternatives have long existed – in the open-source sector, decentralized, independent. What's missing is someone to select them for you, set them up, explain them. That's exactly what we do.

    We are not a classic agency. We are a movement. A network of developers, designers, and strategists who want to put technology back into the hands of those who need it.

    And we work for you – so you can be there for your customers.

    You get a real person, not just a tool. Consultation and implementation, instead of licenses and gag contracts.

    Because in the end, it's not about technology.

    It's about dignity.

    It's about your work, your visibility, your digital freedom.
  `.split('\n\n').map(paragraph => paragraph.trim()).filter(p => p);

  return (
    <main className="pt-24 pb-16">
      {/* Header Section */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Our Manifest"
            subtitle="Our stance on digital self-determination"
            centered
          />
          <div className="flex justify-center mt-8 space-x-4">
            <button 
              onClick={() => setViewMode('verse')}
              className={`px-4 py-2 rounded-md font-lexend text-sm ${
                viewMode === 'verse' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Verse Form
            </button>
            <button 
              onClick={() => setViewMode('prose')}
              className={`px-4 py-2 rounded-md font-lexend text-sm ${
                viewMode === 'prose' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Prose Form
            </button>
            <a 
              href="#" 
              className="px-4 py-2 rounded-md font-lexend text-sm bg-gray-800 text-white hover:bg-gray-700 flex items-center"
              onClick={(e) => e.preventDefault()}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </a>
          </div>
        </div>
      </section>

      {/* Manifest Content */}
      <section 
        ref={(el) => sectionsRef.current[0] = el}
        className="py-16 bg-gradient-to-br from-slate-50 via-ocean-50 to-slate-50 dark:from-deepblue-900 dark:via-ocean-900/20 dark:to-deepblue-900 transition-all duration-700"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {viewMode === 'verse' ? (
              <div className="space-y-8">
                <h2 className="text-center font-lexend font-bold text-2xl mb-8 fade-in">📜 Manifest Version 1.1</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {verseManifest.map((item) => (
                    <div 
                      key={item.number}
                      className="bg-white dark:bg-deepblue-800 p-8 rounded-lg shadow-lg transform hover:-translate-y-2 transition-all duration-500 fade-in group relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-ocean-100/20 to-transparent dark:from-ocean-500/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                      <div className="relative z-10">
                        <div className="text-6xl font-bold text-ocean-100 dark:text-ocean-900/50 mb-6 font-lexend">
                          {item.number.toString().padStart(2, '0')}
                        </div>
                        <h3 className="font-lexend font-semibold text-xl text-slate-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors mb-4">
                          {item.headline}
                        </h3>
                        <div className="space-y-2">
                          {item.text.split('\n').map((line, idx) => (
                            <p key={idx} className="text-slate-600 dark:text-slate-300">{line}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-12 pt-8 border-t border-gray-200 text-center fade-in">
                  <div className="font-lexend font-bold text-2xl flex items-center justify-center text-dark dark:text-white">
                    <span className="mr-2">✊</span> Welcome to Fork:it.
                  </div>
                  <div className="font-lexend font-bold text-2xl text-ocean-600 dark:text-ocean-400 mt-2">
                    The system is yours.
                  </div>
                </div>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none dark:prose-invert">
                <h2 className="text-center font-lexend font-bold text-2xl mb-8">📜 Manifest Version 1.1</h2>
                <p className="text-center mb-12">Prose Form</p>
                
                {proseManifest.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 dark:text-gray-300 mb-6 text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
                
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="font-lexend font-bold text-2xl flex items-center justify-center text-dark dark:text-white">
                    <span className="mr-2">✊</span> Welcome to Fork:it.
                  </div>
                  <div className="font-lexend font-bold text-2xl text-center text-ocean-600 dark:text-ocean-400 mt-2">
                    The system is yours.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gray-50 dark:bg-deepblue-800">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-lexend font-bold text-3xl text-dark dark:text-white mb-6">
              Share Our Vision
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              If you agree with our manifest, help us change the digital world.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="#" 
                className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-md font-lexend font-medium text-base shadow-sm transition-all"
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied!');
                }}
              >
                Share Manifest
              </a>
              <a 
                href="/waitlist" 
                className="bg-transparent border border-primary text-primary dark:text-ocean-400 px-6 py-3 rounded-md font-lexend font-medium text-base hover:bg-primary/5 dark:hover:bg-ocean-900/30 transition-all"
              >
                Join Waitlist
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ManifestPage; 